import type { ClienteApi } from '../cliente';
import { claveIdempotencia, consulta, negocio, type Pagina } from '../comun';
import type { components, operations } from '../tipos/plataforma';

type IdNegocio = components['schemas']['BusinessId'];

/** Operaciones de la pantalla «Actividad» (1.19), con su `operationId` y los tipos generados del contrato. */
export type EntradaActividad = components['schemas']['AuditEntry'];
export type ListaActividad = components['schemas']['AuditEntryList'];
export type CategoriaActividad = components['schemas']['ActivityCategory'];
export type FiltroExportacion = components['schemas']['ActivityFilter'];
export type PermisoSoporte = components['schemas']['SupportWriteGrant'];
export type ListaPermisosSoporte = components['schemas']['SupportWriteGrantList'];
export type NuevoPermisoSoporte = components['schemas']['SupportWriteGrantCreate'];
export type ConsultaActividad = Omit<NonNullable<operations['listActivity']['parameters']['query']>, 'cursor' | 'limit'>;

export function operacionesActividad(api: ClienteApi) {
    return {
        /** listActivity (`actividad.ver`): de la más reciente a la más antigua; `category` y `q` (G183); cursor sin total (G121). */
        listActivity: (businessId: IdNegocio, filtro: ConsultaActividad = {}, pagina: Pagina = {}) =>
            api.pedir<ListaActividad>('GET', `${negocio(businessId)}/activity${consulta({ ...filtro, ...pagina })}`),
        /** exportActivity (`actividad.ver`): 202, se avisa con un enlace que caduca. La propia exportación queda en Actividad. */
        exportActivity: (businessId: IdNegocio, filtro: FiltroExportacion, clave = claveIdempotencia()) =>
            api.pedir<undefined>('POST', `${negocio(businessId)}/activity/exports`, { cuerpo: filtro, idempotencia: clave }),
        /** listSupportWriteGrants (`actividad.ver`): el permiso vigente (`active`) o los anteriores (`ended`) de Soporte (G73). */
        listSupportWriteGrants: (businessId: IdNegocio, estado: 'active' | 'ended', pagina: Pagina = {}) =>
            api.pedir<ListaPermisosSoporte>('GET', `${negocio(businessId)}/support-write-grants${consulta({ status: estado, ...pagina })}`),
        /** getSupportWriteGrant (`actividad.ver`). */
        getSupportWriteGrant: (businessId: IdNegocio, grantId: string) =>
            api.pedir<PermisoSoporte>('GET', `${negocio(businessId)}/support-write-grants/${encodeURIComponent(grantId)}`),
        /**
         * createSupportWriteGrant (`negocio.soporte_escritura.conceder`, solo el propietario): como mucho 24 h; con otro
         * vigente, 409. `x-two-factor: session` (G116): úsese con `conDosPasosEnElMomento` y su `clave`.
         */
        createSupportWriteGrant: (businessId: IdNegocio, datos: NuevoPermisoSoporte, clave = claveIdempotencia()) =>
            api.pedir<PermisoSoporte>('POST', `${negocio(businessId)}/support-write-grants`, { cuerpo: datos, idempotencia: clave }),
        /** revokeSupportWriteGrant (`negocio.soporte_escritura.conceder`): corta la siguiente escritura de Soporte en el acto. */
        revokeSupportWriteGrant: (businessId: IdNegocio, grantId: string, clave = claveIdempotencia()) =>
            api.pedir<undefined>('DELETE', `${negocio(businessId)}/support-write-grants/${encodeURIComponent(grantId)}`, { idempotencia: clave }),
    };
}
